import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { NOT_RECORDED } from "../lib/counters";
import { avgText, toRateText } from "../lib/recordFigures";
import { leftOutNote } from "../lib/overs";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Trophy, Target, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

const NAVY = "#0B2E59", GOLD = "#D4A017", BLUE = "#84C5FF", GREEN = "#10B981", RED = "#EF4444", SLATE = "#64748B";
const PIE_COLORS = [NAVY, GOLD, BLUE, GREEN, RED, SLATE, "#8B5CF6", "#EC4899"];
const AXIS = { fontSize: 11, fill: SLATE };
const TT = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };

// Issue #55: season/competition are optional — with neither prop supplied
// (the default "all"), the fetch carries no query params and behaves
// exactly as before (unfiltered career totals), so standalone/no-prop call
// sites are unaffected. Issue #78 adds `team` the same way — Dashboard.jsx
// must pass its own team filter here too, otherwise the summary tiles above
// (filtered) and this deep-dive (unfiltered) would disagree, the exact bug
// flagged in review on #55.
// Issue #762 — the season record's five deep-analytics folds (ruling 6:
// "fill them from the existing CareerDashboard tabs… reuse them, don't
// rewrite") need to open on the fold the player tapped, not always on
// Batting. `initialTab` is the smallest change that lets a caller pick —
// one optional prop, defaulted to the tab this component has always opened
// on, so `Dashboard.jsx`'s own untouched call site (line ~752, no
// `initialTab` passed) renders byte-for-byte what it always has. The tab
// strip itself is still the player's own to move around after that; this
// only seeds where it starts.
// #1129 — `hideTabs`: the Record screen's folds already name the panel they open
// on, so a second strip of tabs inside an open fold is a choice nobody needs
// (the nav guard counts it). The panel for `initialTab` still renders.
// Issue #901 (D141) introduced `hideUnrecordedKeeping` because the WK tab
// had no real "has this account ever kept" signal to draw on; #704 gives it
// one (`matches.kept_wicket`), so the tab now always shows the real
// numbers and this prop no longer changes its rendering. Left in the
// signature, accepted and unused, rather than removed: `SeasonRecordScreen.
// jsx`'s five folds still pass it, and dropping the prop from ITS five call
// sites is not this issue's job to do in the same diff as the fix.
export default function CareerDashboard({ hideHeader = false, season = "all", competition = "all", team = "all", initialTab = "batting", hideTabs = false, hideUnrecordedKeeping: _hideUnrecordedKeeping = false }) {
  const [d, setD] = useState(null);
  // With the tabs hidden there is no tablist, so a tabpanel is the wrong role and
  // nothing labels it by id: it becomes a named region instead.
  const panelProps = (name) => (hideTabs ? { role: "region", "aria-labelledby": undefined, "aria-label": name } : {});

  useEffect(() => {
    const params = new URLSearchParams();
    if (season !== "all") params.set("season", season);
    if (competition !== "all") params.set("competition", competition);
    if (team !== "all") params.set("team", team);
    const qs = params.toString();
    api.get(`/analytics/career${qs ? `?${qs}` : ""}`).then((r) => setD(r.data));
  }, [season, competition, team]);

  if (!d) return <div className="text-slate-500">Loading…</div>;
  const { batting, bowling, wicketkeeping: wk, development: dev, milestones, trend } = d;
  const headingSuffix = [season !== "all" ? season : null, competition !== "all" ? competition : null, team !== "all" ? team : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-6" data-testid="career-dashboard">
      {!hideHeader && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Deep analytics</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1" data-testid="career-dashboard-heading">
            Career Dashboard{headingSuffix && <span className="text-slate-400"> · {headingSuffix}</span>}
          </h1>
          <p className="text-slate-500 mt-1">Every angle of your performance, visualised.</p>
        </div>
      )}

      <Tabs defaultValue={initialTab} data-testid="career-tabs">
        {!hideTabs && (
        <TabsList>
          <TabsTrigger value="batting" data-testid="tab-batting">Batting</TabsTrigger>
          <TabsTrigger value="bowling" data-testid="tab-bowling">Bowling</TabsTrigger>
          <TabsTrigger value="wk" data-testid="tab-wk">Wicket-keeping</TabsTrigger>
          <TabsTrigger value="dev" data-testid="tab-dev">Development</TabsTrigger>
        </TabsList>
        )}

        {/* ---------- BATTING ---------- */}
        <TabsContent value="batting" className="space-y-6 mt-6" {...panelProps("Batting")}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <MetricTile label="Career Runs" value={batting.career_runs} color={NAVY} tid="bat-runs" />
            <MetricTile label="Average" value={avgText(batting.career_avg)} color={GOLD} tid="bat-avg" />
            <MetricTile label="Strike Rate" value={batting.career_sr ?? NOT_RECORDED} color={BLUE} tid="bat-sr" />
            <MetricTile label="Highest" value={batting.highest} color={NAVY} tid="bat-hs" />
            <MetricTile label="50s" value={batting.fifties} color={GOLD} tid="bat-50s" />
            <MetricTile label="100s" value={batting.hundreds} color={GOLD} tid="bat-100s" />
            <MetricTile label="Conversion" value={`${batting.conversion_rate}%`} color={GREEN} tid="bat-conv" />
            <MetricTile label="Not Outs" value={batting.not_outs} color={SLATE} tid="bat-no" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard title="Career progression (cumulative runs)" full testid="chart-career-progression">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gProg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={NAVY} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Area type="monotone" dataKey="cum_runs" stroke={NAVY} strokeWidth={2.5} fill="url(#gProg)" name="Cumulative runs" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Batting average by position" testid="chart-by-pos">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={batting.by_position}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="position" tick={AXIS} label={{ value: "Batting position", position: "insideBottom", offset: -4, style: { fontSize: 11, fill: SLATE } }} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="avg" fill={GOLD} radius={[6,6,0,0]} name="Average" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Dismissal analysis" testid="chart-dismissals">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={batting.dismissals} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={90} label={({ type, count }) => `${type}: ${count}`}>
                    {batting.dismissals.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TT} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Runs by season" testid="chart-runs-season">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={batting.by_season}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="season" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="runs" fill={NAVY} radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <HeatmapCard title="Runs by ground (heatmap)" data={batting.by_ground} testid="heatmap-grounds" />
          </div>
        </TabsContent>

        {/* ---------- BOWLING ---------- */}
        <TabsContent value="bowling" className="space-y-6 mt-6" {...panelProps("Bowling")}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <MetricTile label="Wickets" value={bowling.wickets} color={GOLD} tid="bo-wkts" />
            <MetricTile label="Bowl Avg" value={avgText(bowling.avg)} color={NAVY} tid="bo-avg" />
            <MetricTile label="Economy" value={bowling.econ ?? NOT_RECORDED} color={BLUE} tid="bo-econ" />
            <MetricTile label="Bowl SR" value={bowling.sr ?? NOT_RECORDED} color={NAVY} tid="bo-sr" />
            <MetricTile label="Best Figures" value={bowling.best_figures} color={GOLD} tid="bo-best" />
            <MetricTile label="Maidens" value={bowling.maidens} color={SLATE} tid="bo-md" />
            <MetricTile label="Dot Balls" value={bowling.dots} color={SLATE} tid="bo-dots" />
            <MetricTile label="Overs" value={bowling.overs} color={SLATE} tid="bo-overs" />
          </div>
          {/* D260: the economy and overs above leave out spells with unreadable overs. */}
          {leftOutNote(bowling.overs_left_out) && (
            <p className="text-xs text-slate-600" data-testid="bo-overs-left-out">
              Economy, strike rate and overs: {leftOutNote(bowling.overs_left_out)}.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Seasonal performance — wickets" testid="chart-wkts-season">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={bowling.by_season}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="season" tick={AXIS} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="wickets" fill={GOLD} radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Economy trend across career" testid="chart-econ-trend">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} />
                  <Tooltip contentStyle={TT} />
                  <Line type="monotone" dataKey="econ" stroke={BLUE} strokeWidth={3} dot={{ fill: NAVY, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Wicket analysis (cumulative)" testid="chart-cum-wkts">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gWkts" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="match" tick={AXIS} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip contentStyle={TT} />
                  <Area type="monotone" dataKey="cum_wkts" stroke={GOLD} strokeWidth={2.5} fill="url(#gWkts)" name="Cumulative wickets" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Wickets by ground" testid="chart-wkts-ground">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={bowling.by_ground} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" tick={AXIS} allowDecimals={false} />
                  <YAxis dataKey="ground" type="category" tick={{ ...AXIS, fontSize: 10 }} width={110} />
                  <Tooltip contentStyle={TT} />
                  <Bar dataKey="wickets" fill={NAVY} radius={[0,6,6,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </TabsContent>

        {/* ---------- WICKETKEEPING ---------- */}
        <TabsContent value="wk" className="space-y-6 mt-6" {...panelProps("Keeping")}>
          {/* Issue #704 — "Matches kept" and "Dismissals / Match" now count
              `matches.kept_wicket === true`
              (`api/src/routes/statsCompute.ts`), never `matches.length`.
              `hideUnrecordedKeeping` (the season record's five-fold embed,
              `SeasonRecordScreen.jsx`) no longer hides either tile: the
              flag is real, so a player who has kept zero matches under the
              current filter is an honest `0`/"—", not an unknown. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <MetricTile label="Total Dismissals" value={wk.dismissals} color={NAVY} tid="wk-dis" />
            <MetricTile label="Catches" value={wk.catches} color={GOLD} tid="wk-c" />
            <MetricTile label="Stumpings" value={wk.stumpings} color={BLUE} tid="wk-st" />
            <MetricTile label="Byes Conceded" value={wk.byes_conceded} color={SLATE} tid="wk-b" />
            <MetricTile label="Dismissals / Match" value={wk.dismissals_per_match ?? NOT_RECORDED} color={GREEN} tid="wk-dpm" />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Wicket-keeping summary</h3>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={[
                      { name: "Catches", value: wk.catches },
                      { name: "Stumpings", value: wk.stumpings },
                    ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} label>
                      <Cell fill={GOLD} /><Cell fill={BLUE} />
                    </Pie>
                    <Legend />
                    <Tooltip contentStyle={TT} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  <Row k="Matches kept" v={wk.matches} />
                  <Row k="Catches taken" v={wk.catches} />
                  <Row k="Stumpings" v={wk.stumpings} />
                  <Row k="Byes conceded" v={wk.byes_conceded} />
                  {/* #727 — the keeper's own run outs and assists, kept off
                      "Total dismissals" on purpose: that figure is catches +
                      stumpings and always has been, and a run out is
                      effected by a throw rather than taken behind the
                      stumps. Splitting the counters is not a licence to move
                      a number nobody asked to have moved. */}
                  <Row k="Run outs (wk)" v={wk.run_outs} />
                  <Row k="Assists (wk)" v={wk.assists} />
                  <Row k={<>Total dismissals <span className="text-xs font-normal text-slate-500">(catches + stumpings)</span></>} v={wk.dismissals} highlight />
                  <Row k="Dismissals per match" v={wk.dismissals_per_match ?? NOT_RECORDED} highlight />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- DEVELOPMENT ---------- */}
        <TabsContent value="dev" className="space-y-6 mt-6" {...panelProps("Development")}>
          <div className="grid md:grid-cols-2 gap-6">
            <DevCard title="Batting average — trend"
              first={dev.batting_first_avg} last={dev.batting_last_avg} delta={dev.batting_delta}
              improveIsUp={true} tid="dev-bat" formatValue={avgText} />
            <DevCard title="Bowling economy — trend"
              first={dev.econ_first} last={dev.econ_last} delta={dev.econ_delta}
              improveIsUp={false} tid="dev-bo" formatValue={toRateText}
              note={leftOutNote(bowling.overs_left_out)} />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={18} color={GOLD} />
                <h3 className="font-display font-bold text-lg text-slate-900">Milestones achieved</h3>
              </div>
              {milestones.length === 0 ? (
                <p className="text-sm text-slate-500">No milestones yet — keep going!</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3" data-testid="milestones-list">
                  {milestones.map((m, i) => (
                    <div key={i} className="p-4 rounded-xl border border-slate-200 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}20` }}>
                        <Sparkles size={16} color={GOLD} />
                      </div>
                      {/* #251: `m.ground` is a ground name — free text,
                          arbitrarily long, beside a fixed-size icon. */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{m.type}</div>
                        <div className="text-sm text-slate-600 break-words">{m.value}{m.ground ? ` at ${m.ground}` : ""}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{m.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Target size={18} color={NAVY} />
                <h3 className="font-display font-bold text-lg text-slate-900">Performance goals</h3>
              </div>
              <p className="text-sm text-slate-600">Track your development goals with progress bars, milestones and status.</p>
              <a href="/development" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-[#0B2E59] hover:underline" data-testid="dev-goto-goals">
                Manage development plan <ArrowUpRight size={14} />
              </a>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricTile({ label, value, color, tid }) {
  return (
    <Card className="border-slate-200" data-testid={tid}>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <div className="font-display font-black text-2xl mt-1" style={{ color }}>{value}</div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, testid, full }) {
  return (
    <Card className={`border-slate-200 ${full ? "lg:col-span-3" : ""}`} data-testid={testid}>
      <CardContent className="p-6">
        <h3 className="font-display font-bold text-base text-slate-900 mb-4">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className={`flex justify-between items-center py-2 border-b border-slate-100 ${highlight ? "text-[#0B2E59] font-semibold" : "text-slate-700"}`}>
      <span className="text-sm">{k}</span>
      <span className="font-display font-bold text-lg">{v}</span>
    </div>
  );
}

/**
 * D184 (issue #963, PR #968 review F1) — `formatValue` is the caller's own
 * average/economy formatter (`avgText` for the batting card, `toRateText`
 * for the economy card, keeping economy at two decimals — see
 * `recordFigures.js`'s own header on why those stay two separate
 * functions), applied to `first`/`last` AND to the delta tile's magnitude.
 * The delta's SIGN is still the arrow icon + `Math.abs` this card already
 * used — `formatValue` only decides how many decimals the number itself
 * carries, never the direction, so no second "delta" formatter is needed
 * (rule: one shared seam, not two). `first`/`last` are only ever read here
 * once `hasData` is true (both non-null), so `formatValue` never actually
 * sees `null` in this component — but it would still resolve to the same
 * em dash `avgText`/`toRateText` give any other null caller, the same "no
 * average yet" rule this card was quietly exempt from before this fix.
 */
function DevCard({ title, first, last, delta, improveIsUp, tid, formatValue, note = null }) {
  const hasData = first != null && last != null;
  const improved = improveIsUp ? delta > 0 : delta < 0;
  const noChange = delta === 0;
  const color = !hasData ? SLATE : noChange ? SLATE : improved ? GREEN : RED;
  const Icon = !hasData ? TrendingUp : improved ? TrendingUp : TrendingDown;
  return (
    <Card className="border-slate-200" data-testid={tid}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
            <Icon size={20} color={color} />
          </div>
        </div>
        {!hasData ? (
          <p className="text-sm text-slate-500">Not enough matches to compute trend yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[10px] uppercase text-slate-500">1st half</div><div className="font-display font-black text-2xl text-slate-700">{formatValue(first)}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">2nd half</div><div className="font-display font-black text-2xl text-slate-900">{formatValue(last)}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Delta</div>
                <div className="font-display font-black text-2xl flex items-center justify-center gap-1" style={{ color }}>
                  {improved ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />} {formatValue(Math.abs(delta))}
                </div>
              </div>
            </div>
            <p className={`mt-4 text-sm ${improved ? "text-emerald-600" : "text-rose-600"} font-medium`}>
              {improved ? "Trending upward — keep the momentum." : "Slight decline — review recent form."}
            </p>
          </>
        )}
        {/* D260: the halves leave out spells with unreadable overs. */}
        {note && <p className="mt-2 text-xs text-slate-600" data-testid={`${tid}-left-out`}>Economy trend: {note}.</p>}
      </CardContent>
    </Card>
  );
}

function HeatmapCard({ title, data, testid }) {
  if (!data || data.length === 0) return (
    <ChartCard title={title} testid={testid}><p className="text-sm text-slate-500 py-8">No data yet.</p></ChartCard>
  );
  const max = Math.max(...data.map(d => d.runs));
  return (
    <ChartCard title={title} testid={testid}>
      <div className="grid grid-cols-2 gap-2">
        {data.map((g) => {
          const intensity = g.runs / max;
          const bg = `rgba(11, 46, 89, ${0.15 + intensity * 0.75})`;
          return (
            <div key={g.ground} className="p-3 rounded-lg" style={{ background: bg }}>
              <div className="text-[10px] uppercase tracking-wider text-white/70">{g.matches} match{g.matches !== 1 && "es"}</div>
              <div className="text-xs text-white font-semibold truncate">{g.ground}</div>
              <div className="font-display font-black text-xl text-white mt-1">{g.runs}</div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}
